import { Bell, Search, Menu, Sun, Moon, ChevronRight, LogOut } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/auth/AuthContext";
import { fetchProfile } from "@/api/userAccount";
import { BrandLogo } from "@/components/BrandLogo";

interface TopBarProps {
  onMenuClick: () => void;
}

const breadcrumbMap: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/courses": "Courses",
  "/course": "Course",
  "/lesson": "Lesson",
  "/exam": "Exam",
  "/mock-tests": "Mock Tests",
  "/mock-test-bundles": "Mock Test Bundles",
  "/admin/mock-test-purchases": "Test Purchases",
  "/admin/mock-test-subscriptions": "Subscriptions",
  "/admin/mock-tests/new": "New mock test",
  "/admin/courses": "Courses",
  "/admin/mock-tests": "Mock Tests",
  "/admin/exam-config": "Exam Config",
  "/admin/mock-test-bundles": "Mock Test Bundles",
  "/exam-history": "Exam History",
  "/community": "Community",
  "/certificates": "Certificates",
  "/settings": "System Settings",
  "/profile-settings": "Profile Settings",
  "/password-change": "Password Change",
  "/teacher": "Dashboard",
  "/teacher/courses": "Courses",
  "/teacher/create-exam": "Create Exam",
  "/teacher/students": "Students",
  "/admin": "Dashboard",
  "/admin/create-course": "Create Course",
  "/admin/users": "Users",
  "/admin/roles": "Roles & Permissions",
  "/admin/certificates": "Certificates",
};

const notifications = [
  { id: 1, text: "New student enrolled in N3 Mastery", time: "5m ago", unread: true },
  { id: 2, text: "Exam results available for Quiz #5", time: "1h ago", unread: true },
  { id: 3, text: "Community: 3 new replies in #grammar-help", time: "2h ago", unread: false },
  { id: 4, text: "Certificate issued for N5 Foundation", time: "1d ago", unread: false },
];

export function TopBar({ onMenuClick }: TopBarProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // const hideBreadcrumbs = location.pathname.startsWith("/community");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  // Build breadcrumbs
  const pathname = location.pathname;
  let breadcrumbs: { label: string; path: string }[] = [];

  // Special cases for courses/exams/mock-tests to keep clean hierarchy
  if (pathname === "/courses" || pathname === "/teacher/courses" || pathname === "/admin/courses") {
    breadcrumbs = [{ label: "Courses", path: pathname }];
  } else if (pathname === "/admin/create-course") {
    breadcrumbs = [
      { label: "Courses", path: "/admin/courses" },
      { label: "Create Course", path: pathname },
    ];
  } else if (pathname === "/admin/mock-tests/new") {
    breadcrumbs = [
      { label: "Mock Tests", path: "/mock-tests" },
      { label: "New Mock Test", path: pathname },
    ];
  } else if (pathname === "/admin/create-exam" || pathname === "/teacher/create-exam") {
    const searchParams = new URLSearchParams(location.search);
    const courseId = searchParams.get("courseId");
    const baseCoursesPath = pathname.startsWith("/admin") ? "/admin/courses" : "/teacher/courses";
    if (courseId) {
      const structurePath = pathname.startsWith("/admin")
        ? `/admin/courses/${courseId}/structure`
        : `/teacher/courses/${courseId}/structure`;
      breadcrumbs = [
        { label: "Courses", path: baseCoursesPath },
        { label: "Course Structure", path: structurePath },
        { label: "Create Exam", path: pathname + location.search },
      ];
    } else {
      breadcrumbs = [
        { label: "Courses", path: baseCoursesPath },
        { label: "Create Exam", path: pathname },
      ];
    }
  } else if (
    (pathname.startsWith("/admin/courses/") && pathname.endsWith("/structure")) ||
    (pathname.startsWith("/teacher/courses/") && pathname.endsWith("/structure"))
  ) {
    const baseCoursesPath = pathname.startsWith("/admin") ? "/admin/courses" : "/teacher/courses";
    breadcrumbs = [
      { label: "Courses", path: baseCoursesPath },
      { label: "Course Structure", path: pathname },
    ];
  } else {
    const pathSegments = pathname.split("/").filter(Boolean);
    let currentPath = "";
    for (const seg of pathSegments) {
      currentPath += "/" + seg;
      const label = breadcrumbMap[currentPath];
      if (label) {
        const isDashboard = label === "Dashboard";
        const isLast = currentPath === pathname;
        if (!isDashboard || isLast) {
          breadcrumbs.push({ label, path: currentPath });
        }
      }
    }
  }

  const unreadCount = notifications.filter(n => n.unread).length;
  const roleLabel = user?.role ? user.role[0].toUpperCase() + user.role.slice(1) : "";
  const profileQuery = useQuery({
    queryKey: ["profile_topbar"],
    queryFn: () => fetchProfile(),
    enabled: Boolean(user),
    staleTime: 60 * 1000,
  });

  const profile = profileQuery.data?.profile ?? null;
  const firstName = profile?.firstName?.trim() ?? "";
  const lastName = profile?.lastName?.trim() ?? "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const emailPrefix = user?.email ? user.email.split("@")[0] : "";
  const displayName = fullName || emailPrefix || "User";
  const avatarUrl = profile?.image ?? null;

  return (
    <header className="h-14 flex items-center justify-between border-b border-border px-4 md:px-6 bg-card/90 backdrop-blur-sm sticky top-0 z-30">
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={onMenuClick} className="lg:hidden text-muted-foreground hover:text-foreground">
          <Menu size={20} />
        </button>

        {/* Breadcrumbs */}
        {(
          <nav className="hidden md:flex items-center gap-1.5 text-sm min-w-0">
            {breadcrumbs.length === 0 ? (
              <span className="font-medium text-foreground truncate">Home</span>
            ) : (
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
                Home
              </Link>
            )}
            {breadcrumbs.map((bc, i) => (
              <span key={bc.path} className="flex items-center gap-1.5">
                <ChevronRight size={12} className="text-muted-foreground/50" />
                {i === breadcrumbs.length - 1 ? (
                  <span className="font-medium text-foreground truncate">{bc.label}</span>
                ) : (
                  <Link
                    to={bc.path}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {bc.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        )}

        {/* Mobile logo */}
        <div className="flex items-center gap-2 lg:hidden">
          <BrandLogo asLinkTo="/" size="sm" />
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {/* Search */}
        {/* <div className={cn(
          "transition-all duration-200 overflow-hidden",
          searchOpen ? "w-48 md:w-64" : "w-0"
        )}>
          <input
            type="text"
            placeholder="Search courses, users..."
            className="w-full h-8 px-3 text-sm bg-secondary rounded-lg outline-none text-foreground placeholder:text-muted-foreground"
            onBlur={() => setSearchOpen(false)}
            autoFocus={searchOpen}
          />
        </div> */}

        {/* Dark mode */}
        <button
          onClick={() => setDarkMode(!darkMode)}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
        >
          {darkMode ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notifications */}
        {/* <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors relative">
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-destructive rounded-full" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex justify-between items-center">
              <span>Notifications</span>
              {unreadCount > 0 && <Badge variant="secondary" className="text-[10px]">{unreadCount} new</Badge>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.map(n => (
              <DropdownMenuItem key={n.id} className="flex-col items-start gap-1 py-3 cursor-pointer">
                <p className={cn("text-sm", n.unread ? "font-medium text-foreground" : "text-muted-foreground")}>{n.text}</p>
                <p className="text-[11px] text-muted-foreground">{n.time}</p>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu> */}

        {/* Account */}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-1 h-9 px-2.5 flex items-center gap-2 rounded-lg bg-secondary text-sm text-foreground hover:bg-accent transition-colors">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    className="w-6 h-6 rounded-full object-cover bg-primary/10 shrink-0"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[10px] font-bold shrink-0">
                    {(displayName?.[0] ?? "U").toUpperCase()}
                  </div>
                )}
                <div className="hidden sm:flex flex-col items-start leading-none">
                  <span className="text-xs font-semibold max-w-[120px] truncate">{displayName}</span>
                  <span className="text-[10px] text-muted-foreground capitalize">{user.role}</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="space-y-0.5">
                <div className="text-sm font-semibold text-foreground truncate">{displayName}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                <div className="text-xs text-muted-foreground capitalize">{roleLabel}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile-settings" className="cursor-pointer">
                  Profile Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/password-change" className="cursor-pointer">
                  Password Change
                </Link>
              </DropdownMenuItem>
              {user.role === "admin" && (
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    System Settings
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className={cn("cursor-pointer")}
                onClick={() => void logout()}
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
